import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { LuDatabase, LuFilter, LuPlus, LuSearch } from "react-icons/lu";
import { toast } from "react-toastify";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import Pagination from "../../components/pagination";
import UserFormModal from "../../components/users/UserFormModal";
import UserPermissionModal from "../../components/users/UserPermissionModal";
import UserList from "../../components/users/UserList";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import cx from "../../components/ui/cx";
import {
  actionBarClass,
  cardClass,
  cardContentClass,
  cardHeaderClass,
  cardTitleClass,
  fieldIconClass,
  fieldInputClass,
  filterFooterClass,
  filterGridClass,
  filterLabelClass,
  pageTitleClass,
} from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchUsersListData,
  getEditUser,
  getUsers,
  getTotalUsers,
  setEditUser,
  toggleUserModal,
} from "../../features/users/usersSlice";
import {
  getListState,
  saveListState,
} from "../../features/listState/listStateSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { DELETE, POST, PUT } from "../../services/axios.service";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";
import { IFormInput } from "../../types/users";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "યુઝર ની વિગતો" },
];

const listKey = "users";

const UsersPage = () => {
  const dispatch = useAppDispatch();
  const users = useAppSelector(getUsers);
  const totalUsers = useAppSelector(getTotalUsers);
  const editUser = useAppSelector(getEditUser);
  const loggingUser = useAppSelector(getLoggingUser);
  const savedList = useAppSelector((state) => getListState(state, listKey));

  const canCreate = hasPermission(
    loggingUser?.permissions,
    permission.usersCreate
  );

  interface FilterFormInput {
    name: string;
    fromDate: string;
    toDate: string;
  }

  const emptyFilters: FilterFormInput = {
    name: "",
    fromDate: "",
    toDate: "",
  };

  const { handleSubmit, register, reset, getValues } =
    useForm<FilterFormInput>({ defaultValues: savedList.filters });

  const [page, setPage] = useState<number>(savedList.page || 1);
  const [pageSize, setPageSize] = useState<number>(savedList.pageSize || 50);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showFilter, setShowFilter] = useState<boolean>(
    !!savedList.showFilter
  );

  const fetchId = useRef(0);

  const paginate = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const handleShowUserForm = async () => {
    await dispatch(setEditUser(null));
    dispatch(toggleUserModal(true));
  };

  const handleSaveUser = async (data: IFormInput) => {
    try {
      if (editUser && editUser.id) {
        await PUT(`/v1/admin/users/${editUser.id}`, {
          name: data.name,
          email: data.email,
          phone: data.phone,
          password: data.password,
          status: data.status,
        })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              await dispatch(setEditUser(null));

              if (page === 1) {
                fetchUsersList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      } else {
        await POST("/v1/admin/users", {
          name: data.name,
          email: data.email,
          phone: data.phone,
          password: data.password,
          status: data.status,
        })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              if (page === 1) {
                fetchUsersList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      }
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }
  };

  const handleDeleteUser = async (id: number) => {
    try {
      await DELETE(`/v1/admin/users/${id}`)
        .then(async (response: any) => {
          if (response.data.status === "success") {
            const pageNumber = users.length > 1 ? page : page - 1;
            if (page === pageNumber || pageNumber < 1) {
              fetchUsersList(page, pageSize);
            } else {
              setPage(pageNumber);
            }

            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }
  };

  const handleOnFilterSubmit = async () => {
    if (page === 1) {
      fetchUsersList(page, pageSize);
    } else {
      setPage(1);
    }
  };

  const handleClearFilter = () => {
    reset(emptyFilters);
    if (page === 1) {
      fetchUsersList(page, pageSize);
    } else {
      setPage(1);
    }
  };

  const fetchUsersList = async (page: number, size: number) => {
    const data = getValues();
    const currentFetch = ++fetchId.current;

    dispatch(saveListState({ key: listKey, value: { filters: data } }));
    setIsLoading(true);

    dispatch(
      fetchUsersListData({
        page: page,
        size: size,
        search: data?.name || null,
        fromDate: data?.fromDate || null,
        toDate: data?.toDate || null,
      })
    ).then((action: any) => {
      const result = action?.payload?.data;

      if (currentFetch === fetchId.current && result?.status !== "success") {
        toast.error(
          result?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
      }

      // A saved page that is now past the end (its rows deleted, say) moves to the last page.
      const lastPage = Math.max(
        1,
        Math.ceil((Number(result?.data?.total_records) || 0) / size)
      );

      if (
        currentFetch === fetchId.current &&
        page > 1 &&
        result?.status === "success" &&
        !result?.data?.records?.length &&
        lastPage < page
      ) {
        setPage(lastPage);
        return;
      }

      setIsLoading(false);
    });
  };

  // Coming back to the list (from a record's page, say) shows the same page and filters.
  useEffect(() => {
    dispatch(
      saveListState({ key: listKey, value: { page, pageSize, showFilter } })
    );
  }, [dispatch, page, pageSize, showFilter]);

  useEffect(() => {
    fetchUsersList(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>યુઝર ની વિગતો</h1>
      </div>

      <div className="space-y-4 md:space-y-6">
        <div className={actionBarClass}>
          <div
            className={cx(
              "grid w-full gap-2 sm:ml-auto sm:flex sm:w-auto",
              canCreate ? "grid-cols-2" : "grid-cols-1"
            )}
          >
            <Button variant="outline" onClick={() => setShowFilter(!showFilter)}>
              <LuFilter className="h-4 w-4" />
              ફિલ્ટર
            </Button>

            {canCreate && (
              <Button variant="primary" onClick={handleShowUserForm}>
                <LuPlus className="h-4 w-4" />
                નવો યુઝર ઉમેરો
              </Button>
            )}
          </div>
        </div>

        {showFilter && (
          <div className={cardClass}>
            <div className={cardHeaderClass}>
              <div className={`${cardTitleClass} text-lg`}>ફિલ્ટર</div>
            </div>

            <div className={cardContentClass}>
              <form onSubmit={handleSubmit(handleOnFilterSubmit)}>
                <div className={filterGridClass}>
                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ થી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        className={fieldInputClass()}
                        {...register("fromDate")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ સુધી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        className={fieldInputClass()}
                        {...register("toDate")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>શોધો</label>
                    <div className="relative">
                      <LuSearch className={fieldIconClass} />
                      <Input
                        placeholder="વિગતો દ્વારા શોધો"
                        className={fieldInputClass()}
                        {...register("name")}
                      />
                    </div>
                  </div>
                </div>

                <div className={filterFooterClass}>
                  <Button
                    type="button"
                    variant="outline"
                    className="max-sm:flex-1"
                    onClick={handleClearFilter}
                  >
                    ફિલ્ટર સાફ કરો
                  </Button>

                  <Button
                    type="submit"
                    variant="primary"
                    className="max-sm:flex-1"
                  >
                    ફિલ્ટર લાગુ કરો
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        <UserList
          users={users}
          pageNumber={page}
          pageSize={pageSize}
          isLoading={isLoading}
          handleDeleteUser={handleDeleteUser}
        />

        {!isLoading && users.length > 0 && (
          <Pagination
            itemsPerPage={pageSize}
            totalItems={totalUsers}
            paginate={paginate}
            currentPage={page}
            onPageSizeChange={handlePageSizeChange}
            itemLabel="યુઝર"
          />
        )}
      </div>

      <UserFormModal handleSaveUser={handleSaveUser} />

      <UserPermissionModal />
    </AppLayout>
  );
};
export default UsersPage;
